/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import {
  Form,
  FormInstance,
  Input,
  Pagination,
  Row,
  Table,
  TableColumnType,
  TableProps,
} from "antd";
import { Rule } from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import { GetRowKey } from "rc-table/lib/interface";
import React, {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ItemCompletionType } from '../../features/completion/completionSlice'
import {
  RecipeItemInput,
  RecipeItemInputRef,
} from '../../features/treatment/editor/components/RecipeItemInput'
import { Material } from '../../models/material'
import { Page } from '../../models/page'
import styles from './EditableList.module.css'

declare module 'react' {
  function forwardRef<A, B>(
    Component: (props: A) => B
  ): (props: A) => ReactElement | null
}

interface EditableListContextData {
  form?: FormInstance<any>
  refs: Record<string, React.RefObject<HTMLDivElement>>
}

const EditableListContext = React.createContext<EditableListContextData>({
  refs: {},
})

export interface EditableColumnType<RecordType extends object>
  extends TableColumnType<RecordType> {
  editable?: boolean
  autoFocus?: boolean | ((r: RecordType) => boolean)
  nextDataIndex?: string
  inputType?: 'text' | 'material'
  completionType?: ItemCompletionType
  rules?: Rule[]
  storehouseId?: string
  editor?: React.ReactNode
  onSave?: (record: RecordType, values: any) => void
  onSelectMaterial?: (record: RecordType, material: Material) => void
}

interface EditableCellProps<RecordType extends object>
  extends React.HTMLAttributes<HTMLElement> {
  editable: boolean
  autoFocus: boolean | ((r: RecordType) => boolean)
  nextDataIndex?: string
  inputType?: 'text' | 'material'
  completionType?: ItemCompletionType
  rules?: Rule[]
  storehouseId?: string
  title: string
  dataIndex: string
  record: RecordType
  children: React.ReactNode
  onSave?: (record: RecordType, values: any) => void
  onSelectMaterial?: (record: RecordType, material: Material) => void
}

const EditableRow = (props: any): ReactElement => {
  const [form] = useForm()
  return (
    <Form form={form} component={false} autoComplete='off'>
      <EditableListContext.Provider value={{ form, refs: {} }}>
        <tr {...props} />
      </EditableListContext.Provider>
    </Form>
  )
}

function EditableCell<RecordType extends object>({
  editable,
  autoFocus,
  nextDataIndex,
  inputType,
  completionType,
  rules,
  storehouseId,
  dataIndex,
  record,
  children,
  onSave,
  onSelectMaterial,
  ...others
}: EditableCellProps<RecordType>): ReactElement {
  const context = useContext(EditableListContext)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const inputRef = useRef<Input>(null)

  const itemRef = useRef<RecipeItemInputRef>(null)

  const refs = context.refs

  if (editable && dataIndex) {
    refs[dataIndex] = wrapperRef
  }

  const [isEditing, setIsEditing] = useState(
    autoFocus instanceof Function ? autoFocus(record) : !!autoFocus
  )

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      itemRef.current?.focus()
    }
  }, [isEditing])

  const toggle = () => {
    setIsEditing(!isEditing)
    context?.form?.setFieldsValue({ [dataIndex]: (record as any)[dataIndex] })
  }

  const save = async (moveToNext: boolean) => {
    try {
      let values
      try {
        values = await context?.form?.validateFields()
      } catch (e) {
        values = context?.form?.getFieldsValue()
        const errors = context?.form?.getFieldError(dataIndex)
        if (errors && errors.length > 0) {
          return
        }
        values = { [dataIndex]: values[dataIndex] }
      }
      toggle()
      onSave && onSave(record, values)
      if (moveToNext && nextDataIndex) {
        const nextRef = refs[nextDataIndex]
        nextRef?.current?.click()
      }
    } catch (e) {
      // do nothing.
    }
  }

  return (
    <td {...others}>
      {editable ? (
        isEditing ? (
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            className={styles.input}
            rules={rules}
          >
            {inputType === 'material' && completionType ? (
              <RecipeItemInput
                ref={itemRef}
                type={completionType}
                storehouseId={storehouseId}
                filters={false}
                useSpecifiedBatch={true}
                onSelect={(m) => {
                  toggle()
                  onSelectMaterial && onSelectMaterial(record, m)
                }}
                onPressEnter={() => {
                  if (nextDataIndex) {
                    const nextRef = refs[nextDataIndex]
                    nextRef?.current?.click()
                  }
                }}
              />
            ) : (
              <Input
                ref={inputRef}
                onPressEnter={() => save(true)}
                onKeyDown={(e) => {
                  if (e.key !== 'Tab') {
                    return
                  }
                  e.stopPropagation()
                  e.preventDefault()
                  save(true)
                }}
                onBlur={() => save(false)}
              />
            )}
          </Form.Item>
        ) : (
          <div ref={wrapperRef} onClick={toggle} className={styles.wrapper}>
            {children}
          </div>
        )
      ) : (
        children
      )}
    </td>
  )
}

interface EditableListProp<RecordType extends object>
  extends TableProps<RecordType> {
  page?: Page<RecordType>
  columns: EditableColumnType<RecordType>[]
  rowKey?: GetRowKey<RecordType>
  showQuickJumper?: boolean
  showSizeChanger?: boolean
  summary?: () => ReactNode
  onChangePage?: (current: number, size?: number) => void
}

export function EditableList<RecordType extends object>({
  page,
  pagination = undefined,
  dataSource,
  onChangePage,
  columns,
  rowKey = (record: RecordType) => (record as any).id,
  className,
  showQuickJumper = true,
  showSizeChanger = true,
  ...others
}: EditableListProp<RecordType>): ReactElement {
  const mergedColumns = columns?.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: RecordType) => ({
        record,
        editable: col.editable,
        autoFocus: col.autoFocus,
        nextDataIndex: col.nextDataIndex,
        inputType: col.inputType,
        completionType: col.completionType,
        rules: col.rules,
        storehouseId: col.storehouseId,
        title: col.title,
        dataIndex: col.dataIndex,
        onSave: col.onSave,
        onSelectMaterial: col.onSelectMaterial,
      }),
    } as EditableColumnType<RecordType>;
  });

  return (
    <>
      <Table<RecordType>
        components={{
          body: {
            row: EditableRow,
            cell: EditableCell,
          },
        }}
        // scroll={{ y: "100%" }}
        dataSource={page ? page.items : dataSource}
        columns={mergedColumns}
        rowKey={rowKey}
        pagination={false}
        {...others}
        className={`${className || ""} ${styles.table}`}
      />
      {page && pagination !== false && (
        <Row
          justify="end"
          align="middle"
          style={{
            height: "60px",
            padding: "0 15px",
          }}
        >
          <Pagination
            showQuickJumper={showQuickJumper}
            showSizeChanger={showSizeChanger}
            pageSize={page.size}
            current={page.current}
            total={page.total}
            onChange={onChangePage}
            className={styles.pagination}
            showTotal={(total) => `共 ${total} 条`}
          />
        </Row>
      )}
    </>
  );
}
